import { useNavigate, useParams } from "react-router-dom";
import { mockClaims } from "../data/mock-claims";
import { X } from "lucide-react";
import EOBModal from "./eob-modal";
import { useState } from "react";
import MainLayout from "../layout/main-layout";
import { useAuth } from "../context/auth-context";
import Breadcrumbs from "./breadcrumbs";
import { useAuth0 } from "@auth0/auth0-react";

const ClaimDetail = () => {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [showEOB, setShowEOB] = useState(false);
  const auth = useAuth();
  const { user } = useAuth0();

  // Find the claim from mock data
  const claim = mockClaims.find((c) => c.id === Number(claimId));

  const breadcrumbs = [
    { label: "Claims", path: "/claims" },
    { label: claim ? `Claim ${claim.id}` : "Not Found" },
  ];

  const content = !claim ? (
    <div className="p-6 text-center">
      <h2 className="text-xl font-bold mb-4">Claim Not Found</h2>
      <p className="text-gray-600 mb-4">
        The requested claim could not be found.
      </p>
      <button
        onClick={() => navigate("/claims")}
        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
      >
        Return to Claims
      </button>
    </div>
  ) : (
    <>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Claim Details</h2>
          <div className="text-gray-600 space-y-1 mt-1">
            <p>Claim ID: {claim.id}</p>
            <p>Date of Service: {claim.date}</p>
          </div>
        </div>
        <div className="flex gap-4">
          {claim.status.code.startsWith("A4") && (
            <button
              onClick={() => setShowEOB(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              View EOB
            </button>
          )}
          <button
            onClick={() => navigate("/claims")}
            className="p-2 hover:bg-gray-100 rounded-full"
            title="Close"
          >
            <X />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="grid gap-6">
        {/* Patient Information */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="font-bold mb-4">Patient Information</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Patient Name</p>
              <p className="font-medium">{claim.patient}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Patient ID</p>
              <p className="font-medium font-mono">{claim.patientId}</p>
            </div>
          </div>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="font-bold mb-4">Claim Status</h3>
          <span
            className={`px-3 py-1.5 rounded text-sm ${getStatusColorClasses(
              claim.status.code
            )}`}
          >
            {claim.status.desc}
          </span>
        </div>

        {/* Service Details */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="font-bold mb-4">Service Details</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">Procedure</th>
                  <th className="p-2 text-left">Code</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-right">Billed Amount</th>
                  <th className="p-2 text-right">Insurance Pays</th>
                  <th className="p-2 text-right">Patient Pays</th>
                </tr>
              </thead>
              <tbody>
                {claim.services.map((service, index) => (
                  <tr key={index} className="border-b">
                    <td className="p-2">{service.procedure}</td>
                    <td className="p-2 font-mono">{service.procedureCode}</td>
                    <td className="p-2">{service.date}</td>
                    <td className="p-2 text-right">
                      ${service.amount.toLocaleString()}
                    </td>
                    <td className="p-2 text-right">
                      $
                      {claim.status.code.startsWith("A4")
                        ? (service.amount * 0.8).toLocaleString()
                        : "0"}
                    </td>
                    <td className="p-2 text-right">
                      $
                      {claim.status.code.startsWith("A4")
                        ? (service.amount * 0.2).toLocaleString()
                        : "0"}
                    </td>
                  </tr>
                ))}
                <tr className="font-medium border-t">
                  <td className="p-2" colSpan={3}>
                    Total Amount
                  </td>
                  <td className="p-2 text-right">
                    ${claim.totalAmount.toLocaleString()}
                  </td>
                  <td className="p-2 text-right">
                    $
                    {claim.status.code.startsWith("A4")
                      ? (claim.totalAmount * 0.8).toLocaleString()
                      : "0"}
                  </td>
                  <td className="p-2 text-right">
                    $
                    {claim.status.code.startsWith("A4")
                      ? (claim.totalAmount * 0.2).toLocaleString()
                      : "0"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {!claim.status.code.startsWith("A4") && (
            <p className="text-sm text-gray-600 mt-4">
              Insurance and patient portions will be determined once the claim
              is finalized.
            </p>
          )}
        </div>
      </div>

      {/* EOB Modal */}
      {showEOB && <EOBModal claim={claim} onClose={() => setShowEOB(false)} />}
    </>
  );

  return (
    <MainLayout
      username={
        auth.isLoggedIn ? auth.username : user?.name || user?.email || "User"
      }
      onLogout={auth.isLoggedIn ? auth.logout : undefined}
    >
      <div className="p-6 max-w-4xl mx-auto animate-fadeIn">
        <Breadcrumbs items={breadcrumbs} />
        {content}
      </div>
    </MainLayout>
  );
};

// Helper function for status colors (reused from claims-table-row)
const getStatusColorClasses = (statusCode: string) => {
  if (statusCode.startsWith("A4-0")) return "bg-green-100 text-green-800";
  if (statusCode.startsWith("A4-1") || statusCode.startsWith("A3"))
    return "bg-red-100 text-red-800";
  if (statusCode.startsWith("A2")) return "bg-orange-100 text-orange-800";
  return "bg-blue-100 text-blue-800";
};

export default ClaimDetail;
